import React, { useEffect, useMemo, useState } from 'react';
import { Check, Copy, KeyRound, Loader2, Plus, RefreshCw, RotateCw, Save, ShieldOff, X } from 'lucide-react';
import {
  createClientDemo,
  getClientDemos,
  rotateClientDemoKey,
  updateClientDemo,
} from '../../services/api';

const emptyForm = {
  client_name: '',
  slug: '',
  subdomain: '',
  expires_at: '',
  status: 'active',
  notes: '',
};

const toInputDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
};

const fromInputDate = (value) => new Date(value).toISOString();

const ClientDemosManager = () => {
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingDemo, setEditingDemo] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [oneTimeKey, setOneTimeKey] = useState(null);

  const activeCount = useMemo(
    () => demos.filter((demo) => demo.status === 'active').length,
    [demos],
  );

  const loadDemos = async () => {
    setLoading(true);
    setError('');
    try {
      setDemos(await getClientDemos());
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to load client demos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDemos();
  }, []);

  const startCreate = () => {
    setEditingDemo(null);
    setForm(emptyForm);
    setOneTimeKey(null);
    setError('');
  };

  const startEdit = (demo) => {
    setEditingDemo(demo);
    setForm({
      client_name: demo.client_name || '',
      slug: demo.slug || '',
      subdomain: demo.subdomain || '',
      expires_at: toInputDate(demo.expires_at),
      status: demo.status || 'active',
      notes: demo.notes || '',
    });
    setOneTimeKey(null);
    setError('');
  };

  const submitForm = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setOneTimeKey(null);
    try {
      const payload = {
        ...form,
        expires_at: fromInputDate(form.expires_at),
      };
      const result = editingDemo
        ? await updateClientDemo(editingDemo.id, payload)
        : await createClientDemo(payload);
      if (result.access_key) {
        setOneTimeKey({ clientName: result.demo.client_name, key: result.access_key });
      }
      await loadDemos();
      if (!editingDemo) setForm(emptyForm);
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to save this demo.');
    } finally {
      setSaving(false);
    }
  };

  const quickStatus = async (demo, status) => {
    setError('');
    try {
      await updateClientDemo(demo.id, { status });
      await loadDemos();
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to update demo status.');
    }
  };

  const rotateKey = async (demo) => {
    setError('');
    setOneTimeKey(null);
    try {
      const result = await rotateClientDemoKey(demo.id);
      setOneTimeKey({ clientName: result.demo.client_name, key: result.access_key });
      await loadDemos();
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to regenerate access key.');
    }
  };

  const copyKey = async () => {
    if (!oneTimeKey?.key) return;
    await navigator.clipboard?.writeText(oneTimeKey.key);
  };

  return (
    <div className="space-y-6" data-testid="client-demos-manager">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard label="Client demos" value={demos.length} />
        <SummaryCard label="Active demos" value={activeCount} />
        <SummaryCard label="Session length" value="24h default" />
      </div>

      {oneTimeKey && (
        <div className="glass-card p-5 border border-[#00FFD1]/30 bg-[#00FFD1]/10">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold text-[#00FFD1]">Access key shown once</p>
              <p className="mt-1 text-white/60 text-sm">{oneTimeKey.clientName}</p>
              <p className="mt-3 font-mono text-2xl text-white tracking-wide">{oneTimeKey.key}</p>
            </div>
            <button type="button" onClick={copyKey} className="btn-glass-secondary text-sm">
              <Copy className="w-4 h-4" />
              Copy Key
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-400/25 bg-red-400/10 p-4 text-sm text-red-200">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[420px_minmax(0,1fr)] gap-6">
        <form onSubmit={submitForm} className="glass-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white">
              {editingDemo ? 'Edit Demo' : 'Create Demo'}
            </h2>
            {editingDemo && (
              <button type="button" onClick={startCreate} className="text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          <Field label="Client name">
            <input
              className="glass-input"
              value={form.client_name}
              onChange={(event) => setForm({ ...form, client_name: event.target.value })}
              required
            />
          </Field>

          <Field label="Slug">
            <input
              className="glass-input"
              value={form.slug}
              onChange={(event) => setForm({ ...form, slug: event.target.value })}
              required
              placeholder="mxclinic"
            />
          </Field>

          <Field label="Subdomain">
            <input
              className="glass-input"
              value={form.subdomain}
              onChange={(event) => setForm({ ...form, subdomain: event.target.value })}
              required
              placeholder="mxclinic-demo.webouse.com"
            />
          </Field>

          <Field label="Expires at">
            <input
              className="glass-input"
              type="datetime-local"
              value={form.expires_at}
              onChange={(event) => setForm({ ...form, expires_at: event.target.value })}
              required
            />
          </Field>

          <Field label="Status">
            <select
              className="glass-input"
              value={form.status}
              onChange={(event) => setForm({ ...form, status: event.target.value })}
            >
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
              <option value="expired">Expired</option>
            </select>
          </Field>

          <Field label="Notes">
            <textarea
              className="glass-input min-h-[110px]"
              value={form.notes}
              onChange={(event) => setForm({ ...form, notes: event.target.value })}
            />
          </Field>

          <button type="submit" disabled={saving} className="btn-glass-primary w-full text-base">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {editingDemo ? 'Save Demo' : 'Create Demo'}
          </button>
        </form>

        <div className="glass-card p-6">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">Client Demos</h2>
              <p className="text-white/50 text-sm">Access keys are never shown after creation or rotation.</p>
            </div>
            <button type="button" onClick={loadDemos} className="glass-icon-btn" aria-label="Refresh demos">
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-[#00FFD1]" />
            </div>
          ) : demos.length === 0 ? (
            <button
              type="button"
              onClick={startCreate}
              className="w-full rounded-xl border border-dashed border-white/15 p-8 text-center text-white/55 hover:border-[#00FFD1]/40 hover:text-white"
            >
              <Plus className="mx-auto mb-3 h-7 w-7" />
              Create the first client demo
            </button>
          ) : (
            <div className="space-y-4">
              {demos.map((demo) => (
                <DemoRow
                  key={demo.id}
                  demo={demo}
                  onEdit={() => startEdit(demo)}
                  onRotate={() => rotateKey(demo)}
                  onStatus={(status) => quickStatus(demo, status)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value }) => (
  <div className="glass-card p-5">
    <p className="text-white/45 text-sm">{label}</p>
    <p className="mt-2 text-2xl font-bold text-white">{value}</p>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-medium text-white/70">{label}</span>
    {children}
  </label>
);

const DemoRow = ({ demo, onEdit, onRotate, onStatus }) => {
  const statusColor = demo.status === 'active' ? '#00FFD1' : demo.status === 'expired' ? '#FFB800' : '#ff6b6b';

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid={`client-demo-${demo.slug}`}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-semibold text-white">{demo.client_name}</h3>
            <span
              className="rounded-full px-2.5 py-1 text-xs font-semibold capitalize"
              style={{ background: `${statusColor}18`, color: statusColor }}
            >
              {demo.status}
            </span>
          </div>
          <p className="mt-1 text-sm text-white/55">{demo.subdomain}</p>
          <p className="mt-1 text-xs text-white/40">
            {demo.access_count} accesses
            {demo.last_accessed_at ? ` • Last ${new Date(demo.last_accessed_at).toLocaleString()}` : ''}
          </p>
          <p className="mt-1 text-xs text-white/40">
            Expires {demo.expires_at ? new Date(demo.expires_at).toLocaleString() : 'not set'}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onEdit} className="btn-glass-secondary text-sm">
            Edit
          </button>
          <button type="button" onClick={onRotate} className="btn-glass-secondary text-sm">
            <RotateCw className="w-4 h-4" />
            Regenerate Key
          </button>
          {demo.status === 'active' ? (
            <button type="button" onClick={() => onStatus('disabled')} className="btn-glass-secondary text-sm">
              <ShieldOff className="w-4 h-4" />
              Disable
            </button>
          ) : (
            <button type="button" onClick={() => onStatus('active')} className="btn-glass-secondary text-sm">
              <Check className="w-4 h-4" />
              Activate
            </button>
          )}
          <a className="btn-glass-secondary text-sm" href={`https://${demo.subdomain}`} target="_blank" rel="noreferrer">
            <KeyRound className="w-4 h-4" />
            Open
          </a>
        </div>
      </div>
    </div>
  );
};

export default ClientDemosManager;
